import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { FileDown, Filter, Printer, Search } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Card } from '@/components/Card/Card';
import { PrintLabelsDialog } from '@/components/PrintLabelsDialog/PrintLabelsDialog';
import { Table } from '@/components/Table/Table';
import {
  consultaEtiquetasKeys,
  fetchConsultaEtiquetas,
  fetchTipoEntradas,
} from '@/services/consultaEtiquetasService';
import { fetchUnidades, impressoraKeys } from '@/services/impressoraService';
import type {
  IConsultaEtiquetasFiltros,
  IConsultaEtiquetasItem,
  IConsultaEtiquetasOpcoesFiltro,
  IConsultaEtiquetasSearch,
} from '@/types/consultaEtiquetas';
import type { IImprimirEtiquetasResponse } from '@/types/etiquetaImpressao';
import { readStoredUser } from '@/utils/tenantScope';
import { useAnalytics, useErrors, useLogger } from '@/observability';
import './consulta-etiquetas.css';

export const Route = createFileRoute('/_authenticated/consulta/etiquetas/')({
  component: ConsultaEtiquetasPage,
});

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [20, 50, 100];

const INITIAL_FILTROS: IConsultaEtiquetasFiltros = {
  codigo_produto: '',
  codigo_barras: '',
  descricao: '',
  lote: '',
  status: '',
  tipo_entrada: '',
  pedido: '',
  nf_entrada: '',
  endereco: '',
  entrada_de: '',
  entrada_ate: '',
  validade_de: '',
  validade_ate: '',
  saida_de: '',
  saida_ate: '',
};

const INITIAL_OPCOES: IConsultaEtiquetasOpcoesFiltro = {
  codigo_produto_exato: false,
  codigo_barras_exato: false,
  lote_exato: false,
};

const INITIAL_SEARCH: IConsultaEtiquetasSearch = {
  filtros: INITIAL_FILTROS,
  opcoesFiltro: INITIAL_OPCOES,
  page: 1,
  limit: DEFAULT_PAGE_SIZE,
};

const TABLE_COLUMNS: string[] = [
  'ID',
  'Cód. Barras',
  'Cód. Produto',
  'Descrição',
  'Lote',
  'Status',
  'Tipo Entrada',
  'Pedido',
  'NF',
  'Data Entrada',
  'Validade',
  'Data Saída',
  'Peso Líquido',
  'Endereço',
  'Estoque Destino',
];

function formatDate(dateString: string | number | undefined): string {
  if (!dateString) return '';
  try {
    if (typeof dateString === 'number') {
      const date = new Date((dateString - 25569 + 1) * 86400 * 1000);
      return date.toLocaleDateString('pt-BR');
    }

    if (typeof dateString === 'string' && dateString.includes('-')) {
      const [year, month, day] = dateString.split('T')[0].split('-');
      const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
      return date.toLocaleDateString('pt-BR');
    }

    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  } catch {
    return String(dateString);
  }
}

function formatExcelDate(excelNumber: number | string | undefined): string {
  if (!excelNumber) return '';
  try {
    const num = typeof excelNumber === 'number' ? excelNumber : parseFloat(excelNumber);
    const date = new Date((num - 25569 + 1) * 86400 * 1000);
    return date.toLocaleDateString('pt-BR');
  } catch {
    return String(excelNumber);
  }
}

function mapEtiquetaParaExportacao(etiqueta: IConsultaEtiquetasItem) {
  return {
    ID: etiqueta.id || '',
    'Código Barras': etiqueta.codigo_barras || '',
    'Código Produto': etiqueta.codigo_produto || '',
    Descrição: etiqueta.descricao || '',
    Lote: etiqueta.lote || '',
    Status: etiqueta.baixada || '',
    'Tipo Entrada': etiqueta.tipo_entrada || '',
    Pedido: etiqueta.pedido || '',
    NF: etiqueta.nf || '',
    'Data Entrada': formatDate(etiqueta.dataentrada_date) || '',
    Validade: formatExcelDate(etiqueta.validade_excel || etiqueta.validade) || '',
    'Data Saída': formatDate(etiqueta.datasaida_date) || '',
    'Peso Líquido': etiqueta.peso_liquido || '',
    Endereço: etiqueta.endereco || '',
    'Estoque Destino': etiqueta.estoque_destino || '',
  };
}

function getStatusBadgeClass(status: string | undefined): string {
  const normalized = (status || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  if (normalized.includes('disponivel')) return 'consulta-etiquetas-status-badge--disponivel';
  if (normalized.includes('reservado')) return 'consulta-etiquetas-status-badge--reservado';
  if (normalized.includes('bloqueado')) return 'consulta-etiquetas-status-badge--bloqueado';
  if (normalized.includes('baixada') || normalized.includes('expedido')) {
    return 'consulta-etiquetas-status-badge--expedido';
  }

  return 'consulta-etiquetas-status-badge--default';
}

function formatStatusLabel(status: string | undefined): string {
  if (!status) return '-';
  if (status.toUpperCase() === 'DISPONIVEL') return 'Disponível';
  if (status === 'Baixada') return 'Expedido';
  return status;
}

const NULL_FILTER_VALUE = '!null';

function toEtiquetaId(id: string | number | undefined): number | null {
  if (id === undefined || id === null || id === '') return null;
  const numericId = typeof id === 'number' ? id : Number(id);
  return Number.isFinite(numericId) ? numericId : null;
}

function mapEtiquetaToLabelData(etiqueta: IConsultaEtiquetasItem): Record<string, unknown> {
  return {
    codigo_barras: etiqueta.codigo_barras ?? '',
    codigo_produto: etiqueta.codigo_produto ?? '',
    descricao: etiqueta.descricao ?? '',
    lote: etiqueta.lote ?? '',
    validade: etiqueta.validade_excel ?? etiqueta.validade ?? '',
    peso_liquido: etiqueta.peso_liquido ?? '',
    endereco: etiqueta.endereco ?? '',
    pedido: etiqueta.pedido ?? '',
    nf: etiqueta.nf ?? '',
    tipo_entrada: etiqueta.tipo_entrada ?? '',
  };
}

function StatusBadge({ status }: { status: string | undefined }) {
  return (
    <span className={`consulta-etiquetas-status-badge ${getStatusBadgeClass(status)}`}>
      {formatStatusLabel(status)}
    </span>
  );
}

function ConsultaEtiquetasPage() {
  const analytics = useAnalytics();
  const errors = useErrors();
  const logger = useLogger();
  const [filtros, setFiltros] = useState<IConsultaEtiquetasFiltros>(INITIAL_FILTROS);
  const [opcoesFiltro, setOpcoesFiltro] =
    useState<IConsultaEtiquetasOpcoesFiltro>(INITIAL_OPCOES);
  const [activeSearch, setActiveSearch] = useState<IConsultaEtiquetasSearch>(INITIAL_SEARCH);
  const [actionMessage, setActionMessage] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [manualUnidadeId, setManualUnidadeId] = useState('');
  const [pedidoSomentePreenchido, setPedidoSomentePreenchido] = useState(false);
  const [nfEntradaSomentePreenchido, setNfEntradaSomentePreenchido] = useState(false);
  const selectAllRef = useRef<HTMLInputElement>(null);

  const { data: tipoEntradaList = [] } = useQuery({
    queryKey: consultaEtiquetasKeys.tipoEntradas,
    queryFn: fetchTipoEntradas,
  });

  const { data: unidades = [] } = useQuery({
    queryKey: impressoraKeys.unidades(),
    queryFn: fetchUnidades,
  });

  const storedUser = readStoredUser();
  const visibleUnidadeIds = useMemo(
    () => new Set(storedUser?.unidades_visiveis.map((unidade) => unidade.id) ?? []),
    [storedUser],
  );

  const availableUnidades = useMemo(() => {
    if (visibleUnidadeIds.size === 0) return unidades;
    return unidades.filter((unidade) => visibleUnidadeIds.has(unidade.id));
  }, [unidades, visibleUnidadeIds]);

  const defaultUnidadeId =
    storedUser?.unidades_visiveis[0]?.id || availableUnidades[0]?.id || '';
  const selectedUnidadeId = manualUnidadeId || defaultUnidadeId;

  const {
    data: listData,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: consultaEtiquetasKeys.list(activeSearch),
    queryFn: () => fetchConsultaEtiquetas(activeSearch),
  });

  const etiquetas = useMemo(() => listData?.etiquetas ?? [], [listData?.etiquetas]);
  const totalRecords = listData?.total ?? 0;
  const pageSize = activeSearch.limit;
  const totalPages = Math.ceil(totalRecords / pageSize) || 0;
  const currentPage = activeSearch.page;
  const inicio = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const fim = Math.min(currentPage * pageSize, totalRecords);

  const handleFilterChange = (field: keyof IConsultaEtiquetasFiltros, value: string) => {
    setFiltros((prev) => ({ ...prev, [field]: value }));
  };

  const handleOpcaoFiltroChange = (
    field: keyof IConsultaEtiquetasOpcoesFiltro,
    value: boolean,
  ) => {
    setOpcoesFiltro((prev) => ({ ...prev, [field]: value }));
  };

  const handleFiltrar = () => {
    setActionMessage('');
    setActiveSearch({
      filtros: {
        ...filtros,
        pedido: pedidoSomentePreenchido ? NULL_FILTER_VALUE : filtros.pedido,
        nf_entrada: nfEntradaSomentePreenchido ? NULL_FILTER_VALUE : filtros.nf_entrada,
      },
      opcoesFiltro: { ...opcoesFiltro },
      page: 1,
      limit: pageSize,
    });
  };

  const handleLimparFiltros = () => {
    setFiltros(INITIAL_FILTROS);
    setOpcoesFiltro(INITIAL_OPCOES);
    setPedidoSomentePreenchido(false);
    setNfEntradaSomentePreenchido(false);
    setActionMessage('');
    setActiveSearch(INITIAL_SEARCH);
  };

  const handlePedidoSomentePreenchidoChange = (checked: boolean) => {
    setPedidoSomentePreenchido(checked);
    if (checked) handleFilterChange('pedido', '');
  };

  const handleNfEntradaSomentePreenchidoChange = (checked: boolean) => {
    setNfEntradaSomentePreenchido(checked);
    if (checked) handleFilterChange('nf_entrada', '');
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setActiveSearch((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newLimit: number) => {
    setActiveSearch((prev) => ({
      ...prev,
      page: 1,
      limit: newLimit,
    }));
  };

  const pageEtiquetaIds = useMemo(
    () =>
      etiquetas
        .map((etiqueta) => toEtiquetaId(etiqueta.id))
        .filter((id): id is number => id !== null),
    [etiquetas],
  );

  const allPageSelected =
    pageEtiquetaIds.length > 0 && pageEtiquetaIds.every((id) => selectedIds.has(id));
  const somePageSelected =
    pageEtiquetaIds.some((id) => selectedIds.has(id)) && !allPageSelected;

  const toggleRowSelection = (id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const toggleSelectAllPage = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      pageEtiquetaIds.forEach((id) => {
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  };

  const handlePrintSuccess = (result: IImprimirEtiquetasResponse) => {
    const falhasMsg =
      result.falhas.length > 0 ? ` Falhas: ${result.falhas.join('; ')}` : '';
    setActionMessage(
      `${result.impressas} etiqueta(s) enviada(s) para impressão.${falhasMsg}`,
    );
    setSelectedIds(new Set());
  };

  const firstSelectedEtiqueta = useMemo(() => {
    const firstId = Array.from(selectedIds)[0];
    if (firstId === undefined) return null;
    return etiquetas.find((etiqueta) => toEtiquetaId(etiqueta.id) === firstId) ?? null;
  }, [etiquetas, selectedIds]);

  const sampleLabelData = firstSelectedEtiqueta
    ? mapEtiquetaToLabelData(firstSelectedEtiqueta)
    : undefined;

  const exportToExcel = async () => {
    if (totalRecords === 0) {
      setActionMessage('Não há dados para exportar. Aplique filtros e carregue os dados primeiro.');
      return;
    }

    setIsExporting(true);
    setActionMessage('Preparando exportação...');

    try {
      const result = await fetchConsultaEtiquetas({
        ...activeSearch,
        page: 1,
        limit: totalRecords,
      });

      const todasEtiquetas = result.etiquetas ?? [];

      if (todasEtiquetas.length === 0) {
        setActionMessage('Nenhum dado encontrado para exportação.');
        return;
      }

      const dadosParaExportar = todasEtiquetas.map(mapEtiquetaParaExportacao);
      const worksheet = XLSX.utils.json_to_sheet(dadosParaExportar);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Consulta Etiquetas');

      worksheet['!cols'] = [
        { wch: 8 },
        { wch: 15 },
        { wch: 15 },
        { wch: 30 },
        { wch: 12 },
        { wch: 12 },
        { wch: 15 },
        { wch: 12 },
        { wch: 10 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 15 },
        { wch: 15 },
      ];

      const agora = new Date();
      const dataHora = agora
        .toLocaleString('pt-BR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })
        .replace(/[/\s:]/g, '_');

      const nomeArquivo = `consulta_etiquetas_${dataHora}.xlsx`;
      XLSX.writeFile(workbook, nomeArquivo);

      analytics.track('Etiquetas Excel Exported', { count: todasEtiquetas.length });
      setActionMessage(
        `Arquivo ${nomeArquivo} exportado com sucesso! Total de ${todasEtiquetas.length} registros.`,
      );
    } catch (exportError) {
      errors.capture(exportError, { tags: { source: 'consulta-etiquetas.export' } });
      logger.error('Etiquetas Excel export failed');
      const msg =
        exportError instanceof Error ? exportError.message : 'Erro desconhecido';
      setActionMessage(`Erro ao exportar arquivo Excel: ${msg}`);
    } finally {
      setIsExporting(false);
    }
  };

  const queryErrorMessage = isError
    ? error instanceof Error
      ? error.message
      : 'Erro ao carregar etiquetas'
    : '';

  const infoMessage =
    !isFetching && !isError && activeSearch && etiquetas.length === 0
      ? 'Nenhum dado encontrado'
      : '';

  const message = actionMessage || queryErrorMessage || infoMessage;
  const loading = isFetching || isExporting;

  const messageClassName = queryErrorMessage || message.includes('Erro')
    ? 'consulta-etiquetas-message--error'
    : isFetching
      ? 'consulta-etiquetas-message--loading'
      : 'consulta-etiquetas-message--info';

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = somePageSelected;
    }
  }, [somePageSelected]);

  return (
    <div className="consulta-etiquetas-container">
      <div className="consulta-etiquetas-content">
        <Card className="filter">
          <Card.Header>
            <div className="consulta-etiquetas-filter-header">
              <Filter
                size={18}
                aria-hidden="true"
              />
              <h3>Filtros de Pesquisa</h3>
            </div>
          </Card.Header>

          <Card.Content className="filter-card__content">
            <div className="consulta-etiquetas-filters-grid">
              <div className="input-content">
                <label htmlFor="codigo-produto">Código Produto</label>
                <input
                  id="codigo-produto"
                  type="text"
                  value={filtros.codigo_produto}
                  onChange={(e) => handleFilterChange('codigo_produto', e.target.value)}
                  placeholder="Ex: PRD-001"
                />
                <label className="consulta-etiquetas-checkbox-label">
                  <input
                    type="checkbox"
                    checked={opcoesFiltro.codigo_produto_exato}
                    aria-label="Busca exata por código do produto"
                    onChange={(e) =>
                      handleOpcaoFiltroChange('codigo_produto_exato', e.target.checked)
                    }
                  />
                  Busca exata
                </label>
              </div>

              <div className="input-content">
                <label htmlFor="codigo-barras">Código Barras</label>
                <input
                  id="codigo-barras"
                  type="text"
                  value={filtros.codigo_barras}
                  onChange={(e) => handleFilterChange('codigo_barras', e.target.value)}
                  placeholder="Ex: 7891234567890"
                />
                <label className="consulta-etiquetas-checkbox-label">
                  <input
                    type="checkbox"
                    checked={opcoesFiltro.codigo_barras_exato}
                    aria-label="Busca exata por código de barras"
                    onChange={(e) =>
                      handleOpcaoFiltroChange('codigo_barras_exato', e.target.checked)
                    }
                  />
                  Busca exata
                </label>
              </div>

              <div className="input-content">
                <label htmlFor="descricao">Descrição</label>
                <input
                  id="descricao"
                  type="text"
                  value={filtros.descricao}
                  onChange={(e) => handleFilterChange('descricao', e.target.value)}
                  placeholder="Descrição do produto"
                />
              </div>

              <div className="input-content">
                <label htmlFor="lote">Lote</label>
                <input
                  id="lote"
                  type="text"
                  value={filtros.lote}
                  onChange={(e) => handleFilterChange('lote', e.target.value)}
                  placeholder="Número do lote"
                />
                <label className="consulta-etiquetas-checkbox-label">
                  <input
                    type="checkbox"
                    checked={opcoesFiltro.lote_exato}
                    aria-label="Busca exata por lote"
                    onChange={(e) => handleOpcaoFiltroChange('lote_exato', e.target.checked)}
                  />
                  Busca exata
                </label>
              </div>

              <div className="input-content">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  value={filtros.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="DISPONIVEL">Disponível</option>
                  <option value="Baixada">Expedido</option>
                </select>
              </div>

              <div className="input-content">
                <label htmlFor="tipo-entrada">Tipo Entrada</label>
                <select
                  id="tipo-entrada"
                  value={filtros.tipo_entrada}
                  onChange={(e) => handleFilterChange('tipo_entrada', e.target.value)}
                >
                  <option value="">Todos</option>
                  {tipoEntradaList.map((tipo) => (
                    <option
                      key={tipo.id}
                      value={tipo.descricao}
                    >
                      {tipo.descricao}
                    </option>
                  ))}
                </select>
              </div>

              <div className="input-content">
                <label htmlFor="pedido">Pedido</label>
                <input
                  id="pedido"
                  type="text"
                  value={filtros.pedido}
                  onChange={(e) => handleFilterChange('pedido', e.target.value)}
                  placeholder="Número do pedido"
                  disabled={pedidoSomentePreenchido}
                />
                <label className="consulta-etiquetas-checkbox-label">
                  <input
                    type="checkbox"
                    checked={pedidoSomentePreenchido}
                    aria-label="Somente pedidos preenchidos"
                    onChange={(e) => handlePedidoSomentePreenchidoChange(e.target.checked)}
                  />
                  Somente preenchidos
                </label>
              </div>

              <div className="input-content">
                <label htmlFor="nf-entrada">NF Entrada</label>
                <input
                  id="nf-entrada"
                  type="text"
                  value={filtros.nf_entrada}
                  onChange={(e) => handleFilterChange('nf_entrada', e.target.value)}
                  placeholder="Nota fiscal"
                  disabled={nfEntradaSomentePreenchido}
                />
                <label className="consulta-etiquetas-checkbox-label">
                  <input
                    type="checkbox"
                    checked={nfEntradaSomentePreenchido}
                    aria-label="Somente notas fiscais preenchidas"
                    onChange={(e) => handleNfEntradaSomentePreenchidoChange(e.target.checked)}
                  />
                  Somente preenchidos
                </label>
              </div>

              <div className="input-content">
                <label htmlFor="endereco">Endereço</label>
                <input
                  id="endereco"
                  type="text"
                  value={filtros.endereco}
                  onChange={(e) => handleFilterChange('endereco', e.target.value)}
                  placeholder="Endereço do estoque"
                />
              </div>

              <div className="input-content consulta-etiquetas-date-range">
                <label htmlFor="validade-de">Validade (De / Até)</label>
                <div className="consulta-etiquetas-date-range__inputs">
                  <input
                    id="validade-de"
                    type="date"
                    name="validade_de"
                    value={filtros.validade_de}
                    onChange={(e) => handleFilterChange('validade_de', e.target.value)}
                  />
                  <span className="consulta-etiquetas-date-range__separator">a</span>
                  <input
                    id="validade-ate"
                    type="date"
                    name="validade_ate"
                    value={filtros.validade_ate}
                    aria-label="Validade até"
                    onChange={(e) => handleFilterChange('validade_ate', e.target.value)}
                  />
                </div>
              </div>

              <div className="input-content consulta-etiquetas-date-range">
                <label htmlFor="entrada-de">Data Entrada (De / Até)</label>
                <div className="consulta-etiquetas-date-range__inputs">
                  <input
                    id="entrada-de"
                    type="date"
                    name="entrada_de"
                    value={filtros.entrada_de}
                    onChange={(e) => handleFilterChange('entrada_de', e.target.value)}
                  />
                  <span className="consulta-etiquetas-date-range__separator">a</span>
                  <input
                    id="entrada-ate"
                    type="date"
                    name="entrada_ate"
                    value={filtros.entrada_ate}
                    aria-label="Data de entrada até"
                    onChange={(e) => handleFilterChange('entrada_ate', e.target.value)}
                  />
                </div>
              </div>

              <div className="input-content consulta-etiquetas-date-range">
                <label htmlFor="saida-de">Data Saída (De / Até)</label>
                <div className="consulta-etiquetas-date-range__inputs">
                  <input
                    id="saida-de"
                    type="date"
                    name="saida_de"
                    value={filtros.saida_de}
                    onChange={(e) => handleFilterChange('saida_de', e.target.value)}
                  />
                  <span className="consulta-etiquetas-date-range__separator">a</span>
                  <input
                    id="saida-ate"
                    type="date"
                    name="saida_ate"
                    value={filtros.saida_ate}
                    aria-label="Data de saída até"
                    onChange={(e) => handleFilterChange('saida_ate', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div
              className="filter-card__divider"
              aria-hidden="true"
            />

            <div className="consulta-etiquetas-filters-actions">
              <button
                type="button"
                className="export-button"
                onClick={() => void exportToExcel()}
                disabled={totalRecords === 0 || loading}
              >
                <FileDown
                  size={16}
                  aria-hidden="true"
                />
                {isExporting ? 'Exportando...' : 'Exportar Excel'}
              </button>

              <button
                type="button"
                className="clear-filter-button"
                onClick={handleLimparFiltros}
              >
                Limpar Filtros
              </button>

              <button
                type="button"
                className="filter-button"
                onClick={handleFiltrar}
              >
                <Search
                  size={16}
                  aria-hidden="true"
                />
                Filtrar
              </button>
            </div>
          </Card.Content>
        </Card>

        {message && (
          <div className={`consulta-etiquetas-message ${messageClassName}`}>
            {message}
          </div>
        )}

        <Card className="consulta-etiquetas-table-card">
          <Card.Header>
            <div className="consulta-etiquetas-table-header">
              <h3>Resultados ({totalRecords.toLocaleString('pt-BR')} registros)</h3>
              <div className="consulta-etiquetas-table-toolbar">
                {availableUnidades.length > 1 && (
                  <div className="consulta-etiquetas-unidade-select">
                    <label htmlFor="consulta-unidade">Unidade</label>
                    <select
                      id="consulta-unidade"
                      value={selectedUnidadeId}
                      onChange={(event) => setManualUnidadeId(event.target.value)}
                    >
                      {availableUnidades.map((unidade) => (
                        <option
                          key={unidade.id}
                          value={unidade.id}
                        >
                          {unidade.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <button
                  type="button"
                  className="consulta-etiquetas-print-button"
                  disabled={selectedIds.size === 0 || !selectedUnidadeId || loading}
                  onClick={() => setPrintDialogOpen(true)}
                >
                  <Printer
                    size={16}
                    aria-hidden="true"
                  />
                  Imprimir
                  {selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
                </button>
              </div>
            </div>
          </Card.Header>

          <Card.Content>
            <div className="consulta-etiquetas-table-container">
              <Table>
                <caption className="sr-only">Consulta de etiquetas</caption>
                <Table.Header>
                  <Table.Row>
                    <Table.Head
                      scope="col"
                      className="consulta-etiquetas-select-col"
                    >
                      <input
                        ref={selectAllRef}
                        type="checkbox"
                        className="consulta-etiquetas-row-checkbox"
                        checked={allPageSelected}
                        disabled={pageEtiquetaIds.length === 0}
                        aria-label="Selecionar todas as etiquetas da página"
                        onChange={(event) => toggleSelectAllPage(event.target.checked)}
                      />
                    </Table.Head>
                    {TABLE_COLUMNS.map((column) => (
                      <Table.Head
                        key={column}
                        scope="col"
                      >
                        {column}
                      </Table.Head>
                    ))}
                  </Table.Row>
                </Table.Header>
                <Table.Content>
                  {etiquetas.length > 0 ? (
                    etiquetas.map((etiqueta, index) => {
                      const etiquetaId = toEtiquetaId(etiqueta.id);
                      const isSelected = etiquetaId !== null && selectedIds.has(etiquetaId);

                      return (
                      <Table.Row key={etiqueta.id ?? index}>
                        <Table.Cell className="consulta-etiquetas-select-col">
                          <input
                            type="checkbox"
                            className="consulta-etiquetas-row-checkbox"
                            checked={isSelected}
                            disabled={etiquetaId === null}
                            aria-label={`Selecionar etiqueta ${etiqueta.id ?? ''}`}
                            onChange={(event) => {
                              if (etiquetaId === null) return;
                              toggleRowSelection(etiquetaId, event.target.checked);
                            }}
                          />
                        </Table.Cell>
                        <Table.Cell>{etiqueta.id}</Table.Cell>
                        <Table.Cell>{etiqueta.codigo_barras || ''}</Table.Cell>
                        <Table.Cell className="consulta-etiquetas-codigo-produto">
                          {etiqueta.codigo_produto || ''}
                        </Table.Cell>
                        <Table.Cell className="consulta-etiquetas-descricao-cell">
                          {etiqueta.descricao || ''}
                        </Table.Cell>
                        <Table.Cell className="consulta-etiquetas-lote-cell">
                          {etiqueta.lote || ''}
                        </Table.Cell>
                        <Table.Cell>
                          <StatusBadge status={etiqueta.baixada} />
                        </Table.Cell>
                        <Table.Cell>{etiqueta.tipo_entrada || ''}</Table.Cell>
                        <Table.Cell>{etiqueta.pedido || ''}</Table.Cell>
                        <Table.Cell>{etiqueta.nf || ''}</Table.Cell>
                        <Table.Cell>{formatDate(etiqueta.dataentrada_date) || ''}</Table.Cell>
                        <Table.Cell>
                          {formatExcelDate(etiqueta.validade_excel || etiqueta.validade) || ''}
                        </Table.Cell>
                        <Table.Cell>{formatDate(etiqueta.datasaida_date) || ''}</Table.Cell>
                        <Table.Cell>{etiqueta.peso_liquido || ''}</Table.Cell>
                        <Table.Cell>{etiqueta.endereco || ''}</Table.Cell>
                        <Table.Cell>{etiqueta.estoque_destino || ''}</Table.Cell>
                      </Table.Row>
                      );
                    })
                  ) : (
                    <Table.Row>
                      <Table.Cell
                        colSpan={TABLE_COLUMNS.length + 1}
                        className="consulta-etiquetas-no-data"
                      >
                        {isFetching ? 'Carregando...' : 'Nenhuma etiqueta encontrada'}
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Content>
              </Table>
            </div>
          </Card.Content>

          {totalRecords > 0 && (
            <Card.Footer className="consulta-etiquetas-pagination">
              <div className="consulta-etiquetas-pagination__left">
                <label htmlFor="page-size">Itens por página:</label>
                <select
                  id="page-size"
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option
                      key={size}
                      value={size}
                    >
                      {size}
                    </option>
                  ))}
                </select>
                <p>
                  Mostrando <strong>{inicio.toLocaleString('pt-BR')}</strong> -{' '}
                  <strong>{fim.toLocaleString('pt-BR')}</strong> de{' '}
                  <strong>{totalRecords.toLocaleString('pt-BR')}</strong> registros
                </p>
              </div>

              <div className="consulta-etiquetas-pagination__controls">
                <button
                  type="button"
                  className="consulta-etiquetas-page-btn"
                  disabled={currentPage === 1 || isFetching}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  ← Anterior
                </button>
                <button
                  type="button"
                  className="consulta-etiquetas-page-btn consulta-etiquetas-page-btn--active"
                  disabled
                >
                  {currentPage}
                </button>
                <button
                  type="button"
                  className="consulta-etiquetas-page-btn"
                  disabled={currentPage >= totalPages || isFetching}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Próximo →
                </button>
              </div>
            </Card.Footer>
          )}
        </Card>

        <PrintLabelsDialog
          open={printDialogOpen}
          onClose={() => setPrintDialogOpen(false)}
          etiquetaIds={Array.from(selectedIds)}
          unidadeId={selectedUnidadeId}
          sampleLabelData={sampleLabelData}
          onSuccess={handlePrintSuccess}
        />
      </div>
    </div>
  );
}
